import * as React from 'react';
import { deleteRequest } from '@/api/requests';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import axiosClient from '@/utils/client';
import { hasAccountRoles, hasCommunityRolesV2, hasRolesV2 } from '@/utils/has-roles';
import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  lighten,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CalendarX as CalendarXIcon,
  ChatCircle as ChatCircleIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
  Clock as ClockIcon,
  FileCsv as CsvIcon,
  Download as DownloadIcon,
  FileXls as ExcelIcon,
  Eye as EyeIcon,
  FileText as FileTextIcon,
  Funnel as FunnelIcon,
  Trash as GarbageIcon,
  House as HouseIcon,
  Paperclip as PaperclipIcon,
  PaperPlaneTilt as PaperPlaneTiltIcon,
  FilePdf as PdfIcon,
  SortAscending as SortIcon,
  User as UserIcon,
  UserPlus as UserPlusIcon,
  Warning as WarningIcon,
  X as XIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useQueryClient } from '@tanstack/react-query';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { Request_Status, type Request } from '@/types/v2/request';
import { type User } from '@/types/v2/user';
import { paths } from '@/paths';
import CreateRequestDialog from '@/pages/v2/requests/request/components/create-request-dialog';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { RouterLink } from '@/components/core/v2/link';

interface ResidentsTableProps {
  rows: Request[];
  loading: boolean;
  error: Error | null;
}

export function RequestsTable({ rows, loading, error }: ResidentsTableProps): React.JSX.Element {
  const { account, community } = useUser();
  const queryClient = useQueryClient();
  const dialog = useDialog();
  const navigate = useNavigate();
  const [exportMenuAnchor, setExportMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = React.useState<null | HTMLElement>(null);

  // Always show all requests (no filtering)
  const filteredRows = React.useMemo(() => {
    return rows;
  }, [rows]);

  // Helper function to check if a request is closed
  const isRequestClosed = (statusName: string | undefined) => {
    if (!statusName) return false;
    const lowerStatus = statusName.toLowerCase();
    return lowerStatus.includes('completed') || lowerStatus.includes('rejected') || lowerStatus.startsWith('closed');
  };

  // Check if user has admin/manager roles
  const hasAdminAccess =
    (account &&
      hasAccountRoles(account, [
        ACCOUNT_ROLES.ACCOUNT_OWNER,
        ACCOUNT_ROLES.ACCOUNT_MANAGER,
        ACCOUNT_ROLES.ACCOUNT_ADMIN,
      ])) ||
    hasCommunityRolesV2(community, [COMMUNITY_ROLES.COMMUNITY_MANAGER]);

  // Helper functions
  const getRequestAge = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyLevel = (status: string | undefined, age: number) => {
    if (!status) return 'low';

    // Closed requests have no urgency
    if (isRequestClosed(status)) return 'closed';

    const urgentStatuses = ['New', 'Initial Review'];
    if (urgentStatuses.includes(status) && age > 7) return 'high';
    if (urgentStatuses.includes(status) && age > 3) return 'medium';
    return 'low';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Export functions
  const exportToCSV = () => {
    const csvData = filteredRows.map((row) => ({
      'Request ID': row.id,
      Status: isRequestClosed(row.request_status?.name) ? 'Closed' : row.request_status?.name || '',
      Type: row.request_type?.name || '',
      Unit: row.unit
        ? `${row.unit.address}, ${row.unit.city} ${row.unit.unit_number ? `#${row.unit.unit_number}` : ''}`
        : 'Community Request',
      Summary: row.summary || '',
      Description: row.description || '',
      // 'Assigned To': row.assigned_to ? `${row.assigned_to.first_name} ${row.assigned_to.last_name}` : 'Unassigned',
      'Created By': row.created_by ? `${row.created_by.first_name} ${row.created_by.last_name}` : '',
      'Created At': formatDate(row.created_at),
      'Age (Days)': isRequestClosed(row.request_status?.name) ? 'N/A' : getRequestAge(row.created_at),
      'Incident Date': row.incident_date ? formatDate(row.incident_date) : '',
      'Files Count': row.files?.length || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Requests');
    XLSX.writeFile(wb, `requests_${new Date().toISOString().split('T')[0]}.csv`);
    setExportMenuAnchor(null);
  };

  const exportToExcel = () => {
    const excelData = filteredRows.map((row) => ({
      'Request ID': row.id,
      Status: row.request_status?.name || '',
      Type: row.request_type?.name || '',
      Unit: row.unit
        ? `${row.unit.address}, ${row.unit.city} ${row.unit.unit_number ? `#${row.unit.unit_number}` : ''}`
        : 'Community Request',
      Summary: row.summary || '',
      Description: row.description || '',
      'Assigned To': row.assigned_to ? `${row.assigned_to.first_name} ${row.assigned_to.last_name}` : 'Unassigned',
      'Created By': row.created_by ? `${row.created_by.first_name} ${row.created_by.last_name}` : '',
      'Created At': formatDate(row.created_at),
      'Age (Days)': isRequestClosed(row.request_status?.name) ? 'N/A' : getRequestAge(row.created_at),
      'Incident Date': row.incident_date ? formatDate(row.incident_date) : '',
      'Files Count': row.files?.length || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Requests');
    XLSX.writeFile(wb, `requests_${new Date().toISOString().split('T')[0]}.xlsx`);
    setExportMenuAnchor(null);
  };

  const columns = React.useMemo<MRT_ColumnDef<Request>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        maxSize: 80,
        enableColumnFilter: false,
        enableSorting: true,
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 80,
            width: 80,
            maxWidth: 80,
          },
        }),
        accessorFn: (row) => {
          // Show first 8 characters of ULID for better readability
          const shortId = row.id.substring(0, 8);

          return (
            <Tooltip placement="top" title={`Full ID: ${row.id}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                <Typography fontFamily="monospace" fontWeight={600} variant="body2">
                  #{shortId}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },

      {
        accessorKey: 'summary',
        header: 'Summary & Description',
        size: 350,
        maxSize: 350,
        grow: true,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'text' as const,
        accessorFn: (row) => {
          const summary = row.summary || '';
          const description = row.description || '';
          const fileCount = row.files?.length || 0;

          return (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Tooltip title={description ? `Description: ${description}` : 'No description'}>
                    <Typography fontWeight={600} sx={{ mb: 0.5 }} variant="body2">
                      {summary.length > 90 ? `${summary.slice(0, 90)}...` : summary}
                    </Typography>
                  </Tooltip>
                </Box>
                {fileCount > 0 && (
                  <Tooltip title={`${fileCount} attachment${fileCount !== 1 ? 's' : ''}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                      <PaperclipIcon color="#666" size={16} />
                      <Typography color="text.secondary" fontWeight={500} variant="caption">
                        {fileCount}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
              {description ? (
                <Typography
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  variant="caption"
                >
                  {description.length > 80 ? `${description.slice(0, 80)}...` : description}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
      {
        accessorKey: 'request_status',
        header: 'Status',
        size: 140,
        maxSize: 140,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'select',
        filterSelectOptions: rows
          .map((row) => row.request_status?.name)
          .filter(Boolean)
          .filter((value, index, self) => self.indexOf(value) === index)
          .map((status) => ({ text: toTitleCase(status || ''), value: status })),
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 140,
            width: 140,
            maxWidth: 140,
          },
        }),
        accessorFn: (row) => {
          const statusName = row?.request_status?.name;
          const isClosed = isRequestClosed(statusName);

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={isClosed ? 'Closed' : toTitleCase(statusName || '')}
                size="small"
                sx={{
                  backgroundColor: isClosed ? 'grey.600' : row?.request_status?.color,
                  color: 'white',
                  fontWeight: 600,
                }}
              />
              {isClosed && statusName !== 'Closed' ? (
                <Tooltip title={`Status: ${toTitleCase(statusName || '')}`}>
                  <Typography color="text.secondary" variant="caption">
                    ({toTitleCase(statusName || '')})
                  </Typography>
                </Tooltip>
              ) : null}
            </Box>
          );
        },
      },

      {
        accessorKey: 'request_type',
        header: 'Type',
        size: 120,
        maxSize: 120,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'select',
        filterSelectOptions: rows
          .map((row) => row.request_type?.name)
          .filter(Boolean)
          .filter((value, index, self) => self.indexOf(value) === index)
          .map((type) => ({ text: toTitleCase(type || ''), value: type })),
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 120,
            width: 120,
            maxWidth: 120,
          },
        }),
        accessorFn: (row) => <Chip label={toTitleCase(row?.request_type?.name || '')} size="small" />,
      },

      // Only show Unit column for admins/managers
      ...(hasAdminAccess
        ? [
            {
              accessorKey: 'unit',
              header: 'Unit',
              size: 120,
              maxSize: 120,
              enableColumnFilter: true,
              enableSorting: true,
              filterVariant: 'text' as const,
              muiTableHeadCellProps: ({ ...rest }) => ({
                ...rest,
                sx: {
                  minWidth: 120,
                  width: 120,
                  maxWidth: 120,
                },
              }),
              accessorFn: (row: Request) => {
                // If it's a community request (no unit), show "Community"
                if (!row.unit_id) {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: 'grey.100',
                          color: 'text.secondary',
                        }}
                      >
                        <HouseIcon size={16} />
                      </Avatar>
                      <Box>
                        <Typography color="text.primary" sx={{ fontWeight: 600, lineHeight: 1.2 }} variant="body2">
                          Community
                        </Typography>
                        <Typography color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }} variant="caption">
                          General Request
                        </Typography>
                      </Box>
                    </Box>
                  );
                }

                // For unit requests, show avatar + two-column layout
                return (
                  <Tooltip placement="top" title={`${row.unit?.address || ''}, ${row.unit?.city || ''}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: 'primary.50',
                          color: 'primary.main',
                          border: '1px solid',
                          borderColor: 'primary.200',
                        }}
                      >
                        <HouseIcon size={16} />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          color="text.primary"
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          variant="body2"
                        >
                          {row.unit?.address?.split(',')[0] || 'Address'}
                        </Typography>
                        {row.unit?.unit_number ? (
                          <Typography
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              lineHeight: 1.1,
                              fontWeight: 500,
                            }}
                            variant="caption"
                          >
                            Unit #{row.unit.unit_number}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Tooltip>
                );
              },
            },
          ]
        : []),
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 120,
        maxSize: 120,
        enableColumnFilter: false,
        enableSorting: true,
        accessorFn: (row) => {
          const age = getRequestAge(row.created_at);
          const urgency = getUrgencyLevel(row.request_status?.name, age);
          const isClosed = isRequestClosed(row.request_status?.name);

          return (
            <Box>
              <Typography sx={{ mb: 0.5 }} variant="body2">
                {formatDate(row.created_at)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isClosed ? (
                  <CheckCircleIcon color="green" size={14} />
                ) : (
                  <CalendarXIcon
                    color={urgency === 'high' ? 'red' : urgency === 'medium' ? 'orange' : 'gray'}
                    size={14}
                  />
                )}
                <Typography
                  color={
                    isClosed
                      ? 'success.main'
                      : urgency === 'high'
                        ? 'error.main'
                        : urgency === 'medium'
                          ? 'warning.main'
                          : 'text.secondary'
                  }
                  fontWeight={urgency === 'high' ? 600 : 400}
                  variant="caption"
                >
                  {isClosed ? 'Closed' : `${age} day${age !== 1 ? 's' : ''}`}
                </Typography>
              </Box>
            </Box>
          );
        },
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 120,
            width: 120,
            maxWidth: 120,
          },
        }),
      },
    ],
    [hasAdminAccess, rows]
  );

  const handleDeleteRequest = async (id: string) => {
    if (!community?.id) {
      toast.error('Community not found');
      return;
    }

    try {
      await deleteRequest(community.id, id);
      // Invaldate the cache
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      toast.success('Request deleted successfully');
      // Optionally refresh the data or remove from the list
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const table = useMaterialReactTable<Request>({
    columns,
    data: filteredRows,
    initialState: {
      showGlobalFilter: true,
      showColumnFilters: false,
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
      },
      sorting: [{ id: 'created_at', desc: true }],
      density: 'compact',
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Search all requests...',
    },
    state: {
      isLoading: loading,
    },
    enableRowActions: false,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableSelectAll: true,
    muiTableContainerProps: (props) => ({
      ...props,
      sx: {
        minHeight: '500px',
      },
    }),
    renderTopToolbar: ({ table }) => {
      return (
        <Box
          sx={(theme) => ({
            backgroundColor: lighten(theme.palette.background.default, 0.05),
            display: 'flex',
            gap: '0.5rem',
            p: '8px',
            justifyContent: 'space-between',
            alignItems: 'center',
          })}
        >
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} />
            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleDensePaddingButton table={table} />

            {/* Active/Closed Toggle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ml: 2,
                pl: 2,
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="currentColor" size={16} />
                <Typography sx={{ fontWeight: 500, color: 'text.secondary' }} variant="body2">
                  All Requests
                </Typography>
              </Box>

              {/* Count Summary */}
              <Typography color="text.secondary" sx={{ ml: 1 }} variant="caption">
                {rows.filter((r) => !isRequestClosed(r.request_status?.name)).length} active, {rows.filter((r) => isRequestClosed(r.request_status?.name)).length} closed of {rows.length} total
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* Export Menu */}
            <Button
              onClick={(event) => {
                setExportMenuAnchor(event.currentTarget);
              }}
              size="small"
              startIcon={<DownloadIcon size={18} />}
              sx={{ textTransform: 'none', fontWeight: 500 }}
              variant="outlined"
            >
              Export
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              onClose={() => {
                setExportMenuAnchor(null);
              }}
              open={Boolean(exportMenuAnchor)}
            >
              <MenuItem onClick={exportToCSV}>
                <ListItemIcon>
                  <CsvIcon size={20} />
                </ListItemIcon>
                <ListItemText>Export to CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToExcel}>
                <ListItemIcon>
                  <ExcelIcon size={20} />
                </ListItemIcon>
                <ListItemText>Export to Excel</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      );
    },
    enablePagination: true, // Disable pagination since we're loading all data
    enableStickyHeader: true,
    enableStickyFooter: false,
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: 'background.paper',
        fontWeight: 600,
        fontSize: '0.875rem',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        navigate(paths.requests.request(row.original.id.toString()));
      },
      sx: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        '& td': {
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
      },
    }),
  });

  return (
    <React.Fragment>
      <Card
        sx={{
          mt: 4,
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Chip color="primary" label={`${rows.length} Total Requests`} size="medium" variant="outlined" />
            <Chip
              color="success"
              label={`${rows.filter((r) => !isRequestClosed(r.request_status?.name)).length} Active`}
              size="medium"
              variant="outlined"
            />
            <Chip
              color="info"
              label={`${rows.filter((r) => isRequestClosed(r.request_status?.name)).length} Closed`}
              size="medium"
              variant="outlined"
            />
          </Stack>

          <Button
            onClick={dialog.handleOpen}
            size="small"
            startIcon={<UserPlusIcon size={18} />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
            variant="contained"
          >
            Create Request
          </Button>
        </Box>
        <MaterialReactTable table={table} />
      </Card>
      <CreateRequestDialog onClose={dialog.handleClose} open={dialog.open} />
    </React.Fragment>
  );
}
